import { useAppDispatch } from 'app/hooks';
import store from 'app/store';
import { captureCdSnapshot } from 'components/watchers/concealed-duct-revert-watcher';
import { ConfigurationType, DuctRunPointType } from 'data/types';
import { setConfigurations } from 'features/configurations';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import { useCallback, useMemo } from 'react';
import {
  findCondenserUniqueIdByZoneUniqueId,
  markCondensersStale
} from 'utils/configurations';
import { DuctRunRef, PointPx, snapPxToGrid } from 'utils/duct-runs';

type ConfigDraft = ConfigurationType;

export function useDuctRunActions(
  configurationId?: string,
  onBeforeCommit?: () => void
) {
  const dispatch = useAppDispatch();

  const commit = useCallback(
    (ref: DuctRunRef, mutateConfig: (config: ConfigDraft) => void) => {
      if (!configurationId) return;
      const { configurations } = store.getState().configurations;
      const activeConfig = configurations.find(
        (config) => config.uniqueId === configurationId
      );
      if (!activeConfig) return;

      captureCdSnapshot(activeConfig, {
        source: 'duct-run-edit',
        zoneUniqueId: ref.zoneUniqueId
      });
      onBeforeCommit?.();

      let updated = produce(configurations, (draft) => {
        const config = draft.find((c) => c.uniqueId === configurationId);
        if (!config) return;
        mutateConfig(config);
      });

      const condenserUniqueId =
        ref.condenserUniqueId ??
        findCondenserUniqueIdByZoneUniqueId(activeConfig, ref.zoneUniqueId);
      if (condenserUniqueId) {
        updated = updated.map((config) =>
          config.uniqueId === configurationId
            ? markCondensersStale(config, [condenserUniqueId])
            : config
        );
      }

      dispatch(setConfigurations(updated));
    },
    [configurationId, onBeforeCommit, dispatch]
  );

  const moveEndpoint = useCallback(
    (ref: DuctRunRef, px: PointPx) => {
      commit(ref, (config) => {
        for (const condenser of config.condensers) {
          for (const zone of condenser.zones) {
            if (zone.uniqueId === ref.zoneUniqueId) {
              if (ref.kind === 'supply') {
                const zoneItem = zone.zoneItems.find(
                  (zi) => zi.structureItemId === ref.spaceUniqueId
                );
                if (zoneItem) {
                  if (ref.runUniqueId === null) {
                    zoneItem.registerOverride = { x: px.x, y: px.y };
                  } else {
                    zoneItem.additionalSupplyRuns = (
                      zoneItem.additionalSupplyRuns ?? []
                    ).map((run) =>
                      run.uniqueId === ref.runUniqueId
                        ? { ...run, x: px.x, y: px.y }
                        : run
                    );
                  }
                }
              } else if (ref.runUniqueId === null) {
                zone.returnOverride = { x: px.x, y: px.y };
              } else {
                zone.additionalReturnRuns = (
                  zone.additionalReturnRuns ?? []
                ).map((run) =>
                  run.uniqueId === ref.runUniqueId
                    ? { ...run, x: px.x, y: px.y }
                    : run
                );
              }
            }
          }
        }
      });
    },
    [commit]
  );

  const updateWaypoints = useCallback(
    (
      ref: DuctRunRef,
      transform: (points: DuctRunPointType[]) => DuctRunPointType[],
      pinReturnEndpointPx?: PointPx,
      basePointsPx?: DuctRunPointType[]
    ) => {
      commit(ref, (config) => {
        for (const condenser of config.condensers) {
          for (const zone of condenser.zones) {
            if (zone.uniqueId === ref.zoneUniqueId) {
              if (ref.kind === 'supply') {
                const zoneItem = zone.zoneItems.find(
                  (zi) => zi.structureItemId === ref.spaceUniqueId
                );
                if (zoneItem) {
                  if (ref.runUniqueId === null) {
                    const next = transform(
                      basePointsPx ?? zoneItem.supplyRunPoints ?? []
                    );
                    zoneItem.supplyRunPoints =
                      next.length > 0 ? next : undefined;
                  } else {
                    zoneItem.additionalSupplyRuns = (
                      zoneItem.additionalSupplyRuns ?? []
                    ).map((run) => {
                      if (run.uniqueId !== ref.runUniqueId) return run;
                      const next = transform(basePointsPx ?? run.points ?? []);
                      return {
                        ...run,
                        points: next.length > 0 ? next : undefined
                      };
                    });
                  }
                }
              } else if (ref.runUniqueId === null) {
                const next = transform(
                  basePointsPx ?? zone.returnRunPoints ?? []
                );
                zone.returnRunPoints = next.length > 0 ? next : undefined;
                if (pinReturnEndpointPx && !zone.returnOverride) {
                  zone.returnOverride = {
                    x: pinReturnEndpointPx.x,
                    y: pinReturnEndpointPx.y
                  };
                }
              } else {
                zone.additionalReturnRuns = (
                  zone.additionalReturnRuns ?? []
                ).map((run) => {
                  if (run.uniqueId !== ref.runUniqueId) return run;
                  const next = transform(basePointsPx ?? run.points ?? []);
                  return { ...run, points: next.length > 0 ? next : undefined };
                });
              }
            }
          }
        }
      });
    },
    [commit]
  );

  const addWaypoint = useCallback(
    (
      ref: DuctRunRef,
      insertIndex: number,
      px: PointPx,
      pinReturnEndpointPx?: PointPx,
      basePointsPx?: DuctRunPointType[]
    ) => {
      const snapped = snapPxToGrid(px);
      const newPoint: DuctRunPointType = {
        uniqueId: nanoid(7),
        x: snapped.x,
        y: snapped.y
      };
      updateWaypoints(
        ref,
        (points) => {
          const boundedIndex = Math.min(
            Math.max(insertIndex, 0),
            points.length
          );
          return [
            ...points.slice(0, boundedIndex),
            newPoint,
            ...points.slice(boundedIndex)
          ];
        },
        pinReturnEndpointPx,
        basePointsPx
      );
    },
    [updateWaypoints]
  );

  const moveWaypoint = useCallback(
    (
      ref: DuctRunRef,
      pointUniqueId: string,
      px: PointPx,
      basePointsPx?: DuctRunPointType[],
      pinReturnEndpointPx?: PointPx
    ) => {
      const snapped = snapPxToGrid(px);
      updateWaypoints(
        ref,
        (points) =>
          points.map((point) =>
            point.uniqueId === pointUniqueId
              ? { ...point, x: snapped.x, y: snapped.y }
              : point
          ),
        pinReturnEndpointPx,
        basePointsPx
      );
    },
    [updateWaypoints]
  );

  const deleteWaypoint = useCallback(
    (
      ref: DuctRunRef,
      pointUniqueId: string,
      basePointsPx?: DuctRunPointType[],
      pinReturnEndpointPx?: PointPx
    ) => {
      updateWaypoints(
        ref,
        (points) => points.filter((point) => point.uniqueId !== pointUniqueId),
        pinReturnEndpointPx,
        basePointsPx
      );
    },
    [updateWaypoints]
  );

  const removeRun = useCallback(
    (ref: DuctRunRef) => {
      if (!ref.runUniqueId) return;
      commit(ref, (config) => {
        for (const condenser of config.condensers) {
          for (const zone of condenser.zones) {
            if (zone.uniqueId === ref.zoneUniqueId) {
              if (ref.kind === 'supply') {
                const zoneItem = zone.zoneItems.find(
                  (zi) => zi.structureItemId === ref.spaceUniqueId
                );
                if (zoneItem) {
                  zoneItem.additionalSupplyRuns = (
                    zoneItem.additionalSupplyRuns ?? []
                  ).filter((run) => run.uniqueId !== ref.runUniqueId);
                }
              } else {
                zone.additionalReturnRuns = (
                  zone.additionalReturnRuns ?? []
                ).filter((run) => run.uniqueId !== ref.runUniqueId);
              }
            }
          }
        }
      });
    },
    [commit]
  );

  return useMemo(
    () => ({
      moveEndpoint,
      addWaypoint,
      moveWaypoint,
      deleteWaypoint,
      removeRun
    }),
    [moveEndpoint, addWaypoint, moveWaypoint, deleteWaypoint, removeRun]
  );
}
